"use client"

import { Text, Button } from "@medusajs/ui"
import { ChevronLeft, ChevronRight } from "@medusajs/icons"
import { formatCount } from "@/lib/util/format-count"

type SearchPaginationProps = {
  currentPage: number
  totalPages: number
  totalHits: number
  onPageChange: (page: number) => void
}

export default function SearchPagination({
  currentPage,
  totalPages,
  totalHits,
  onPageChange,
}: SearchPaginationProps) {
  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1

  return (
    <div className="bg-white rounded-lg shadow-sm border border-navy p-4">
      <div className="flex flex-col items-center gap-3">
        {/* Page Info - Moved to top for mobile */}
        <div className="text-center">
          <Text className="text-sm text-navy">
            Stran {currentPage} od {totalPages} ({formatCount(totalHits)} rezultatov)
          </Text>
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center gap-2">
          {/* Previous Button */}
          <Button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPrevPage}
            variant="secondary"
            className="flex items-center gap-1 px-3 py-2 text-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Prejšnja</span>
          </Button>

          {/* Simplified Page Numbers - Show fewer on mobile */}
          <div className="flex items-center gap-1">
            {(() => {
              // Simplified pagination logic - show fewer pages on mobile
              const pages: (number | string)[] = [];

              if (totalPages <= 5) {
                return Array.from({ length: totalPages }, (_, i) => i + 1);
              }

              // Always show first page
              pages.push(1);

              if (currentPage <= 3) {
                // Show 1, 2, 3, ..., last
                pages.push(2, 3);
                if (totalPages > 4) pages.push("...");
                pages.push(totalPages);
              } else if (currentPage >= totalPages - 2) {
                // Show 1, ..., last-2, last-1, last
                if (totalPages > 4) pages.push("...");
                pages.push(totalPages - 2, totalPages - 1, totalPages);
              } else {
                // Show 1, ..., current-1, current, current+1, ..., last
                if (totalPages > 4) pages.push("...");
                pages.push(currentPage - 1, currentPage, currentPage + 1);
                if (totalPages > 4) pages.push("...");
                pages.push(totalPages);
              }

              return pages;
            })().map((page, index) => (
              <div key={index} className={page !== "..." && page !== 1 && page !== totalPages ? "hidden sm:block" : ""}>
                {page === "..." ? (
                  <span className="px-2 py-2 text-neutral-500 text-sm hidden sm:inline">...</span>
                ) : (
                  <button
                    onClick={() => onPageChange(page as number)}
                    className={`min-w-[32px] h-8 px-2 rounded-md transition-all duration-200 text-sm ${
                      page === currentPage
                        ? "bg-gold text-navy shadow-md"
                        : "bg-white text-navy hover:bg-gray-50 border border-navy"
                    }`}
                  >
                    {page}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Next Button */}
          <Button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNextPage}
            variant="secondary"
            className="flex items-center gap-1 px-3 py-2 text-sm"
          >
            <span className="hidden sm:inline">Naslednja</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
